import { useEffect, useState } from "react";
import { auth } from "./firebase";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";

const ALLOWED_EMAIL = process.env.REACT_APP_ALLOWED_EMAIL || "";
const API_URL       = process.env.REACT_APP_API_URL || "http://localhost:8000";

export function useAuth() {
  const [user,      setUser]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [step,      setStep]      = useState("idle");
  const [qrCode,    setQrCode]    = useState(null);
  const [fbUser,    setFbUser]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        if (ALLOWED_EMAIL && u.email !== ALLOWED_EMAIL) {
          await signOut(auth);
          if (!cancelled) {
            setError("⛔ Acesso negado.");
            setUser(null);
            setFbUser(null);
            setStep("idle");
            setQrCode(null);
            setLoading(false);
          }
          return;
        }
        try {
          const token = await u.getIdToken();
          const resp  = await fetch(`${API_URL}/api/totp/setup`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) {
            const t = await resp.text();
            throw new Error(t || `HTTP ${resp.status}`);
          }
          const data = await resp.json();
          if (cancelled) return;
          setFbUser(u);
          setError(null);
          if (data.configurado) {
            setStep("totp_verify");
          } else {
            setQrCode(data.qr_code);
            setStep("totp_setup");
          }
        } catch (e) {
          if (!cancelled) {
            setError("Erro ao carregar 2FA: " + (e.message || e));
            await signOut(auth);
            setFbUser(null);
            setQrCode(null);
            setStep("idle");
          }
        }
        if (!cancelled) setLoading(false);
        return;
      }
      if (!cancelled) {
        setUser(null);
        setFbUser(null);
        setStep("idle");
        setQrCode(null);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  const login = async (email, senha) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, senha);
    } catch (e) {
      setLoading(false);
      if (
        e.code === "auth/invalid-credential" ||
        e.code === "auth/wrong-password" ||
        e.code === "auth/user-not-found"
      ) {
        setError("❌ Email ou senha incorretos.");
      } else {
        setError("Erro: " + e.message);
      }
    }
  };

  const verificarTotp = async (code) => {
    try {
      setError(null);
      if (!fbUser) {
        setError("Sessão expirada. Entre novamente.");
        return;
      }
      const token = await fbUser.getIdToken();
      const resp  = await fetch(`${API_URL}/api/totp/verify`, {
        method:  "POST",
        headers: {
          "Content-Type":  "application/json",
          Authorization:   `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.detail || data.message || `Erro HTTP ${resp.status}`);
        return;
      }

      if (data.valido) {
        setUser(fbUser);
        setStep("idle");
        setFbUser(null);
        setQrCode(null);
      } else {
        setError("❌ Código inválido. Tente novamente.");
      }
    } catch (e) {
      setError("Erro ao verificar: " + e.message);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setFbUser(null);
    setStep("idle");
    setQrCode(null);
  };

  return {
    user, loading, error, step, qrCode,
    login, verificarTotp, logout,
  };
}

export function LoginPage({ onLogin, error }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [show,  setShow]  = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, senha);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-600 mb-4 shadow-lg shadow-purple-200">
            <span className="text-white text-2xl font-black">M</span>
          </div>
          <h1 className="text-2xl font-black text-gray-900">MOANAZ</h1>
          <p className="text-gray-400 text-sm mt-1 tracking-widest uppercase">
            Forex AI Dashboard
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-xs text-gray-400 uppercase tracking-widest">
              Sistema Ativo
            </span>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">
                Senha
              </label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={senha}
                  onChange={e => setSenha(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all pr-16"
                />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">
                  {show ? "Ocultar" : "Ver"}
                </button>
              </div>
            </div>

            <button type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3.5 rounded-xl transition-all shadow-lg shadow-purple-200 active:scale-95 mt-2">
              Entrar
            </button>
          </form>

          <p className="mt-5 text-center text-xs text-gray-400">
            🔒 Acesso restrito
          </p>
        </div>
      </div>
    </div>
  );
}

export function SetupTotpPage({ qrCode, onVerify, error }) {
  const [code, setCode] = useState("");

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-600 mb-4 shadow-lg shadow-purple-200">
            <span className="text-white text-2xl">🔐</span>
          </div>
          <h1 className="text-xl font-black text-gray-900">
            Configurar Autenticador
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Primeiro acesso — escaneia o QR code
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-6 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold">1</span>
              <p className="text-sm font-semibold text-gray-800">
                Abra o Google Authenticator
              </p>
            </div>
            <p className="text-xs text-gray-400 ml-8">
              Toque em &quot;+&quot; → &quot;Escanear QR code&quot;
            </p>
          </div>

          {qrCode && (
            <div className="flex justify-center">
              <div className="p-3 bg-white border-2 border-purple-200 rounded-xl">
                <img src={qrCode} alt="QR Code" className="w-44 h-44"/>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold">2</span>
              <p className="text-sm font-semibold text-gray-800">
                Digite o código de 6 dígitos
              </p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              placeholder="000000"
              className="w-full text-center text-2xl font-mono tracking-[0.5em] border-2 border-gray-200 focus:border-purple-500 rounded-xl py-3 outline-none"
              maxLength={6}
            />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => onVerify(code)}
            disabled={code.replace(/\D/g, "").length !== 6}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3 rounded-xl transition-all"
          >
            Ativar Autenticador
          </button>
        </div>
      </div>
    </div>
  );
}

export function VerifyTotpPage({ onVerify, error }) {
  const [code, setCode] = useState("");

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-purple-600 mb-4 shadow-lg shadow-purple-200">
            <span className="text-white text-2xl">🔑</span>
          </div>
          <h1 className="text-xl font-black text-gray-900">
            Verificação em 2 etapas
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Digite o código do Google Authenticator
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-6 space-y-4">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
            placeholder="000000"
            className="w-full text-center text-3xl font-mono tracking-[0.5em] border-2 border-gray-200 focus:border-purple-500 rounded-xl py-4 outline-none"
            maxLength={6}
            autoFocus
          />

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => onVerify(code)}
            disabled={code.replace(/\D/g, "").length !== 6}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold py-3.5 rounded-xl transition-all text-base"
          >
            Verificar
          </button>

          <p className="text-center text-xs text-gray-400">
            Abra o Google Authenticator e copie o código
          </p>
        </div>
      </div>
    </div>
  );
}
